// src/ui/components/dialogs/FullscreenDialog.layouts.tsx
import React, { useEffect } from 'react';
import parse from 'html-react-parser';
import AutoFitText from '../../../core/utils/AutoFitText';
import { DialogContentStyles, DialogHeader, SimpleHeader, SpecsContainer, LoadingSpinner } from './FullscreenDialog.components';
import { LayoutProps, VideoLayoutProps, ImageLayoutProps } from './FullscreenDialog.types';
import { isAutoFitDebugActive, addDebugLog } from '../debug/DebugUtils';

/* 🔒 LIMITADOR FÍSICO REAL PARA TVs GRANDES */
const getSafeMaxFontSize = () => {
  const vh = window.innerHeight || 1080;
  const dpr = window.devicePixelRatio || 1;
  const physicalCap = (vh / dpr) * 0.18; // Aumentado de 0.12 a 0.18 para permitir mayor tamaño
  return Math.min(Math.floor(physicalCap), 180); // Aumentado de 120 a 180
};


/* ========================= TEXT ONLY ========================= */
export const TextOnlyLayout: React.FC<LayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
}) => {
  const maxFontSize = getSafeMaxFontSize();

  return (
    <div className="w-full h-full relative flex flex-col px-8 py-6 overflow-hidden">
      <DialogHeader title={title} blockSpecs={blockSpecs} titleStyle="large" />
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 mt-20 mb-4">
        <AutoFitText
          maxHeight="100%"
          maxFontSize={maxFontSize}
          minFontSize={12}
          padding={40}
        >
          <DialogContentStyles />
          <div className="prose max-w-none text-white text-center fullscreen-dialog-content">
            {parse(processedWork || '', parserOptions)}
          </div>
        </AutoFitText>
      </div>
    </div>
  );
};

/* ========================= IMAGE ========================= */
export const ImageLayout: React.FC<ImageLayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
  imageUrl,
}) => {
  const maxFontSize = getSafeMaxFontSize();

  return (
    <div
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url("${imageUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <DialogHeader title={title} blockSpecs={blockSpecs} titleStyle="large" variant="dark" />
      <div className="relative z-10 w-full h-full flex flex-col px-12 py-8 overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center min-h-0 mt-20 mb-4">
          <AutoFitText
            maxHeight="100%"
            maxFontSize={maxFontSize}
            minFontSize={12}
            padding={40}
          >
            <DialogContentStyles />
            <div className="prose max-w-none text-white text-center fullscreen-dialog-content">
              {parse(processedWork || '', parserOptions)}
            </div>
          </AutoFitText>
        </div>
      </div>
    </div>
  );
};

/* ========================= VIDEO ========================= */
export const VideoLayout: React.FC<VideoLayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
  screenResolution,
  mediaHtml,
  shouldLoadMedia,
}) => {
  const maxFontSize = getSafeMaxFontSize();
  const initialFontSize = Math.min(maxFontSize * 0.6, 70);

  useEffect(() => {
    if (isAutoFitDebugActive()) {
      addDebugLog('VideoLayout', 'Layout renderizado', {
        title,
        textScale: screenResolution.textScale,
        isLargeScreen: screenResolution.isLargeScreen,
        maxFontSize,
        initialFontSize,
        minFontSize: 12,
        lineHeight: 1.4,
        contentLength: processedWork?.length || 0,
        hasMedia: !!mediaHtml,
      });
    }
  }, [title, screenResolution.textScale, screenResolution.isLargeScreen, maxFontSize, initialFontSize, processedWork, mediaHtml]);

  return (
    <div className="w-full h-full relative flex flex-col p-6">
      <SimpleHeader title={title} />
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-stretch mt-16 min-h-0">

        <div className="flex flex-col gap-4 min-h-0">
          <div className="w-auto h-auto max-w-full max-h-[60vh] min-w-[45vw] min-h-[40vh] flex items-center justify-center bg-black/20 rounded-lg overflow-hidden">
            {shouldLoadMedia ? parse(mediaHtml || '', parserOptions) : <LoadingSpinner />}
          </div>
          <SpecsContainer specs={blockSpecs?.flat() || []} />
        </div>
        <div className="flex items-start justify-center overflow-hidden">
          <AutoFitText
            alignItems="flex-start"
            maxHeight="100%"
            padding={20}
            minFontSize={12}
            maxFontSize={maxFontSize}
            initialFontSize={initialFontSize}
            lineHeight={1.4}
          >
            <DialogContentStyles />
            <div className="prose max-w-none text-white fullscreen-dialog-content">
              {parse(processedWork || '', parserOptions)}
            </div>
          </AutoFitText>
        </div>
      </div>
    </div>
  );
};

/* ========================= SCALE LAYOUT (NUEVO) ========================= */
export const FullscreenScaleLayout: React.FC<LayoutProps> = ({
  title,
  processedWork,
  parserOptions,
  blockSpecs,
}) => {
  const maxFontSize = getSafeMaxFontSize();

  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col p-8">
      <DialogHeader title={title} blockSpecs={blockSpecs} titleStyle="large" />
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 mt-20">
        <AutoFitText
          maxHeight="100%"
          maxFontSize={maxFontSize}
          minFontSize={12}
          padding={40}
        >
          <DialogContentStyles />
          <div className="prose max-w-none text-white text-center fullscreen-dialog-content">
            {parse(processedWork || '', parserOptions)}
          </div>
        </AutoFitText>
      </div>
    </div>
  );
};

/* ========================= NORMAL ========================= */
export const NormalLayout = (props: any) => {
  // Redirigir a FullscreenScaleLayout para usar el nuevo sistema de escalado inteligente
  return <FullscreenScaleLayout {...props} />;
};
